/**
 * ============================================================================
 * Category Card Component
 * ============================================================================
 * 
 * Carte visuelle pour une catégorie d'émissions avec:
 * - Icône et couleur distinctive
 * - Badge avec nombre de facteurs
 * - Total CO₂ et barre de progression
 * - Hover effects et animations
 * 
 * ============================================================================
 */

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  calculateProgress,
  formatCO2,
  getCategoryDesign,
} from '@/lib/calculator-design';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface CategoryCardProps {
  category: string;
  factorCount: number;
  completedCount?: number;
  totalCO2?: number;
  benchmark?: number;
  onClick?: () => void;
}

export function CategoryCard({
  category,
  factorCount,
  completedCount = 0,
  totalCO2 = 0,
  benchmark,
  onClick,
}: CategoryCardProps) {
  const { t } = useTranslation();
  const design = getCategoryDesign(category);
  const Icon = design.icon;
  const progress = calculateProgress(completedCount, factorCount);
  const co2 = formatCO2(totalCO2);
  const isCompleted = completedCount > 0;
  const benchmarkDiff = benchmark && totalCO2 > 0 ? ((totalCO2 - benchmark) / benchmark) * 100 : null;

  return (
    <Card
      className={`group cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] border-2 ${
        isCompleted
          ? 'border-green-200 bg-green-50/50 hover:border-green-300'
          : 'hover:border-primary/20'
      }`}
      onClick={onClick}
    >
      <CardContent className="p-6">
        {/* Header avec icône et badge */}
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl ${design.bgColor}`}>
            <Icon className={`h-6 w-6 ${design.color}`} />
          </div>
          <Badge variant="secondary" className="text-xs">
            {factorCount} {t('calculator.factors')}
          </Badge>
        </div>

        {/* Titre de la catégorie */}
        <h3 className="font-semibold text-sm mb-3 line-clamp-2 min-h-[2.5rem] group-hover:text-primary transition-colors">
          {category}
        </h3>

        {/* Métriques */}
        <div className="space-y-3">
          {/* Benchmark comparison */}
          {benchmarkDiff !== null && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">{t('calculator.vs_benchmark')}:</span>
              <Badge
                variant={benchmarkDiff > 0 ? 'destructive' : 'default'}
                className="text-xs"
              >
                {benchmarkDiff > 0 ? '+' : ''}{benchmarkDiff.toFixed(0)}%
              </Badge>
            </div>
          )}
          {/* Total CO₂ */}
          {totalCO2 > 0 && (
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{co2.value}</span>
              <span className="text-sm text-muted-foreground">{co2.unit}</span>
            </div>
          )}

          {/* Barre de progression */}
          {factorCount > 0 && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {completedCount}/{factorCount} {t('calculator.completed')}
                </span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}
        </div>

        {/* Flèche d'action */}
        <div className="flex items-center justify-end mt-4 text-muted-foreground group-hover:text-primary transition-colors">
          <span className="text-xs font-medium mr-1">{t('calculator.view_details')}</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
}

